import type { QueryTypes } from '@medplum/core'
import type { Patient, Reference } from '@medplum/fhirtypes'
import type { JSX } from 'react'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useQuery } from '#src/hooks/useQuery.ts'

// Totals barely move while a clinician searches, so each code is counted
// once an hour; the participant's own count is fresher.
const TOTAL_STALE_TIME = 60 * 60 * 1000
const PARTICIPANT_STALE_TIME = 5 * 60 * 1000

const compact = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
})

function useObservationCount(
  system: string,
  code: string,
  subject: Reference<Patient> | undefined,
  enabled: boolean
): number | undefined {
  const medplum = useMedplum()
  const reference = subject?.reference
  const { data } = useQuery({
    queryKey: ['observation-code-count', system, code, reference],
    queryFn: async ({ signal }) => {
      const params: QueryTypes = {
        code: `${system}|${code}`,
        'status:not': 'entered-in-error',
        _summary: 'count',
      }
      if (reference) {
        params.subject = reference
      }
      const bundle = await medplum.search('Observation', params, { signal })
      return bundle.total ?? null
    },
    enabled,
    staleTime: reference ? PARTICIPANT_STALE_TIME : TOTAL_STALE_TIME,
    retry: false,
  })
  return data ?? undefined
}

// How often a code is recorded, for the participant and across the chart,
// read best effort: a row shows its counts once they arrive and nothing when
// a count fails.
export function ObservationCodeCounts({
  system,
  code,
  participant,
}: {
  system: string
  code: string
  participant: Reference<Patient> | undefined
}): JSX.Element | null {
  const here = useObservationCount(system, code, participant, !!participant)
  const total = useObservationCount(system, code, undefined, true)
  if (here === undefined && total === undefined) {
    return null
  }
  const counted = (count: number | undefined): string =>
    count === undefined ? '–' : compact.format(count)
  return (
    <span
      data-observation-counts=""
      className="tabular-nums"
      title={
        participant
          ? `${here ?? '–'} for this participant / ${total ?? '–'} in all`
          : `${total ?? '–'} in all`
      }
    >
      {' · '}
      {participant ? `${counted(here)}/${counted(total)}` : counted(total)} uses
    </span>
  )
}
